<template>
  <div class="reporting-section">
    <div class="section-header">
      <BoostingPageAvatars :pages="pages" size="large" />
      <h2 v-if="global">
        {{ $t('dash.boosting.global') }}
        <span class="section-sub">· {{ $t('dash.boosting.global_pages', { count: pages.length }) }}</span>
      </h2>
      <h2 v-else>{{ pages[0].name }}</h2>
    </div>

    <div class="post-empty" v-if="loading">
      <i class="material-icons spin">hourglass_empty</i>
      <p>{{ $t('dash.boosting.loading') }}</p>
    </div>

    <div class="post-empty" v-else-if="error">
      <i class="material-icons" style="color:#e0d0ea">error_outline</i>
      <p style="color:#c0392b">{{ error }}</p>
      <button class="page-btn" style="width:auto;padding:0 14px" @click="load">{{ $t('dash.reporting.retry') }}</button>
    </div>

    <div class="post-empty" v-else-if="!hasData">
      <i class="material-icons">trending_up</i>
      <p>{{ $t('dash.boosting.empty') }}</p>
    </div>

    <template v-else>
      <div class="metric-row">
        <MetricCard
          :value="totals.invited"
          :previous-value="previous ? previous.invited : null"
          :label="$t('dash.boosting.invited')"
          icon="send"
        />
        <MetricCard
          :value="totals.subscribed"
          :previous-value="previous ? previous.subscribed : null"
          :label="$t('dash.boosting.subscribed')"
          icon="person_add"
        />
        <Tooltip :text="$t('dash.boosting.tooltip.conversion_rate')" position="top">
          <MetricCard
            :value="totals.conversion_rate"
            :previous-value="previous ? previous.conversion_rate : null"
            :label="$t('dash.boosting.conversion_rate')"
            icon="show_chart"
            format="percent"
          />
        </Tooltip>
        <Tooltip :text="$t('dash.boosting.tooltip.pending')" position="top">
          <MetricCard
            :value="totals.pending"
            :label="$t('dash.boosting.pending')"
            icon="hourglass_empty"
          />
        </Tooltip>
      </div>

      <template v-if="graphData.length && graphLabels.length">
        <div class="graph-title">{{ $t('dash.boosting.graph') }}</div>
        <div class="graph-wrap">
          <Graph
            :id="`boosting-${pageIds.join('-')}`"
            :data="graphData"
            :labels="graphLabels"
            :stacked="false"
            height="220px"
            width="100%"
          />
        </div>
      </template>

      <template v-if="recentSubscribers.length">
        <div class="subsection-header">
          <i class="material-icons subsection-icon">group</i>
          <span>{{ $t('dash.boosting.recent_subscribers') }}</span>
        </div>
        <div class="subscriber-list">
          <div class="subscriber-row" v-for="sub in recentSubscribers" :key="`${sub.page_id}-${sub.user_id}`">
            <span class="subscriber-avatar">{{ (sub.user_name || '?')[0].toUpperCase() }}</span>
            <BoostingPageAvatars v-if="global" :pages="pagesFor([sub.page_id])" />
            <a
              v-if="sub.user_url"
              class="subscriber-name"
              :href="sub.user_url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ sub.user_name || sub.user_id }}</a>
            <span v-else class="subscriber-name">{{ sub.user_name || sub.user_id }}</span>
            <span class="subscriber-date">{{ formatDate(sub.subscribed_at) }}</span>
            <a
              v-if="sub.post_id"
              class="subscriber-post"
              :href="`https://www.facebook.com/${sub.post_id}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('dash.boosting.view_post') }}
              <i class="material-icons">open_in_new</i>
            </a>
          </div>
        </div>
      </template>

      <BoostingInvitations :pages="pages" :global="global" @changed="load(true)" />
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import MetricCard from '../reporting-web/panels/MetricCard.vue'
import Graph from '../reporting-web/Graph.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import BoostingInvitations from './BoostingInvitations.vue'
import BoostingPageAvatars from './BoostingPageAvatars.vue'

export default {
  name: 'BoostingPage',
  components: { MetricCard, Graph, Tooltip, BoostingInvitations, BoostingPageAvatars },
  props: {
    pages:     { type: Array, required: true },
    global:    { type: Boolean, default: false },
    dateStart: { type: String, required: true },
    dateEnd:   { type: String, required: true },
    prevRange: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      boosting: null,
    }
  },
  computed: {
    pageIds() { return this.pages.map(p => p.id) },
    hasData() { return !!(this.boosting && this.boosting.has_data) },
    totals() { return (this.boosting && this.boosting.totals) || {} },
    previous() { return (this.boosting && this.boosting.previous) || null },
    recentSubscribers() { return (this.boosting && this.boosting.recent_subscribers) || [] },
    dateFormat() {
      return this.$i18n.locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
    },
    graphPoints() { return (this.boosting && this.boosting.graph) || [] },
    graphLabels() {
      return this.graphPoints.map(d => moment(d.date * 1000).format(this.dateFormat))
    },
    graphData() {
      if (!this.graphPoints.length) return []
      return [
        {
          label: this.$t('dash.boosting.invited_series'),
          data: this.graphPoints.map(d => d.invited || 0),
          type: 'bar',
        },
        {
          label: this.$t('dash.boosting.subscribed_series'),
          data: this.graphPoints.map(d => d.subscribed || 0),
          type: 'bar',
          color: '#10B981',
        },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load(silent) {
      if (silent !== true) this.loading = true
      this.error = null
      try {
        var apiHost = process.env.VUE_APP_API_HOST
        var since = moment(this.dateStart).unix()
        var until = moment(this.dateEnd).endOf('day').unix()
        var tz = new Date().getTimezoneOffset()
        var url = apiHost + '/reporting/boosting?page_ids=' + this.pageIds.join(',') + '&since=' + since + '&until=' + until + '&timezone=' + tz
        if (this.prevRange) url += '&prev_since=' + this.prevRange.since + '&prev_until=' + this.prevRange.until
        var token = localStorage.getItem('token')
        var res = await fetch(url, { headers: { Authorization: token ? ('Bearer ' + token) : '' } })
        var json = await res.json()
        if (!res.ok) throw new Error(json.message || json.error || 'Erreur serveur')
        this.boosting = json.boosting || null
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    pagesFor(ids) {
      return this.pages.filter(p => ids.includes(p.id))
    },
    formatDate(date) {
      return date ? moment(date).format(this.dateFormat) : ''
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.section-sub { font-size: 13px; font-weight: 500; color: #9ba3b7; }
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 20px; }
.metric-row > .hasTooltip {
  flex: 1;
  min-width: 160px;
  display: flex;
}
.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
.graph-wrap { width: 100%; margin-bottom: 8px; }
.subsection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
}
.subsection-icon { font-size: 18px; color: #6B1F4D; }
.subscriber-list { display: flex; flex-direction: column; }
.subscriber-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 4px;
  font-size: 13px;
  color: #121826;
  border-bottom: 1px solid #f1edf5;
}
.subscriber-row:last-child { border-bottom: none; }
.subscriber-avatar {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.subscriber-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: #121826;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}
a.subscriber-name:hover { color: #6B1F4D; }
.subscriber-date { color: #555E75; font-size: 12px; flex-shrink: 0; }
.subscriber-post {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6B1F4D;
  text-decoration: none;
}
.subscriber-post:hover { text-decoration: underline; }
.subscriber-post .material-icons { font-size: 14px; }
.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }
.page-btn {
  display: flex; align-items: center; justify-content: center;
  height: 30px; border-radius: 8px;
  border: 1px solid #e0d0ea; background: #fff; color: #6B1F4D;
  cursor: pointer; transition: all 0.15s;
}
.page-btn:hover { background: #f1edf5; }
.spin { animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
